'use client';

import { FC } from 'react';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../../../statsig/sdk/provider';
import { type TopBarMenuItemProps } from '../TopBarMenuItem/TopBarMenuItem';
import { TopBarMenu } from '../TopBarMenu/TopBarMenu';
import {
  Drawer,
  TriggerResetTwoFaModal,
  TriggerSetupTwoFaModal,
} from '../../../../../common';
import {
  RegularCookies,
  RegularShield,
  RegularScales,
  RegularQuestionCircle,
  RegularCall,
  RegularCertificate,
  RegularGlobe,
} from '../../../../../common/svgIcons';
import { useClientUserInfo } from '../../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { useModalDrawerContext } from '../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import {
  LanguageScreen,
  LanguageTranslations,
} from '../../../../../language/LanguageMenu/LanguageMenu.use-client';
import { useIsBelowScreenWidth } from '../../../../utils/useIsBelowScreenWidth';
import { useTranslation } from 'react-i18next';

export type UserTopBarMenuTranslations = {
  frequentlyAskedQuestions: string;
  customerSupport: string;
  termsAndConditions: string;
  generalLogout: string;
  twoFaWelcomeTitle: string;
  twoFaResetTitle: string;
  reportsAndStatements: string;
  legalDocumentation: string;
  cookies: string;
  language: string;
  accountLevelUpgradeTitle: string;
  accountLevelUpgradeSubtitle: string;
  yourAccount: string;
  islamicBankingAccount: string;
  islamicBankingAccountDescription: string;
  islamicBankingAccountLearnMore: string;
} & LanguageTranslations;

interface ITopBarMenuProps {
  translatedValues: UserTopBarMenuTranslations;
  isWithinDropdown?: boolean;
}

export const UserTopBarMenu: FC<ITopBarMenuProps> = ({
  translatedValues,
  isWithinDropdown,
}) => {
  const { showModal, closeAll, openDrawer } = useModalDrawerContext();

  const isMobile = useIsBelowScreenWidth(1023);

  const { twoFactorAuthentication } = useClientUserInfo() ?? {};

  const isTwoFaSetup = twoFactorAuthentication === 'ENABLED';

  const {
    localPublicFolderPathTo_FAQDocument,
    URLforCustomerSupport,
    appName,
    supportedLanguages,
  } = getAppConfig();

  const feature_twoFactorAuthentication = useFeatureFlag(
    'feature_twoFactorAuthentication',
  );

  const showLanguageSwitcher = supportedLanguages.length > 1;

  const { t } = useTranslation();

  const twoFaClickAction = () => {
    closeAll();

    if (isTwoFaSetup) {
      showModal(TriggerResetTwoFaModal, {
        disableOverlayClose: true,
      });
    } else {
      showModal(TriggerSetupTwoFaModal, {
        disableOverlayClose: true,
      });
    }
  };

  const languageClickAction = () => {
    // ! Client translation is mandatory below, otherwise drawer's title won't be updated when language is changed
    openDrawer(
      () => (
        <Drawer title={t('language')}>
          <LanguageScreen translations={translatedValues} />
        </Drawer>
      ),
      { position: isMobile ? 'bottom' : 'right' },
    );
  };

  const TopBarMenuItems: TopBarMenuItemProps[] = [
    ...(feature_twoFactorAuthentication
      ? [
          {
            icon: <RegularShield size={24} />,
            label: isTwoFaSetup
              ? translatedValues.twoFaResetTitle
              : translatedValues.twoFaWelcomeTitle,
            type: 'button',
            action: twoFaClickAction,
          } as TopBarMenuItemProps,
        ]
      : []),
    ...(appName === 'LBBW'
      ? [
          {
            icon: <RegularQuestionCircle size={24} />,
            label: translatedValues.frequentlyAskedQuestions,
            type: 'downloadLink',
            onItemClick: closeAll,
            action: localPublicFolderPathTo_FAQDocument,
          } as TopBarMenuItemProps,
          {
            icon: <RegularCall size={24} />,
            label: translatedValues.customerSupport,
            type: 'externalLink',
            action: URLforCustomerSupport,
            onItemClick: closeAll,
          } as TopBarMenuItemProps,
        ]
      : []),
    {
      label: translatedValues.reportsAndStatements,
      type: 'link',
      icon: <RegularCertificate size={24} />,
      action: '/reports-and-statements',
      onItemClick: closeAll,
    },
    {
      label: translatedValues.legalDocumentation,
      type: 'link',
      icon: <RegularScales size={24} />, // 'scales',
      action: '/legal-documentation',
      onItemClick: closeAll,
    },
    {
      label: translatedValues.cookies,
      type: 'link',
      icon: <RegularCookies size={24} />,
      action: '/cookie-settings',
      onItemClick: closeAll,
    },
    ...(showLanguageSwitcher
      ? [
          {
            label: translatedValues.language,
            type: 'button',
            icon: <RegularGlobe size={24} />,
            action: languageClickAction,
          } as TopBarMenuItemProps,
        ]
      : []),
  ];

  return (
    <TopBarMenu
      isWithinDropdown={isWithinDropdown}
      TopBarMenuItems={TopBarMenuItems}
      translatedValues={translatedValues}
    />
  );
};
